import React, { useState, useEffect } from "react";
import "../styles/Drawer.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";

const Drawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  items = [],
  onItemClick,
  itemKey = "id",
  itemTitle = "title",
  itemSubtitle = "subtitle",
  courseName = "",
  subjectName = "",
  selectedId = null,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationClass, setAnimationClass] = useState("");
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => {
        setAnimationClass("drawer--open");
      }, 10);
    } else {
      setAnimationClass("drawer--close");
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedId && items.length > 0) {
      const newExpanded = {};
      items.forEach((item) => {
        if (
          item.children &&
          item.children.some((child) => child[itemKey] === selectedId)
        ) {
          newExpanded[item[itemKey]] = true;
        }
      });
      setExpandedItems((prev) => ({ ...prev, ...newExpanded }));
    }
  }, [selectedId, items, itemKey]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const toggleItem = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  if (!shouldRender) return null;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div
        className={`drawer drawer--center ${animationClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer-header">
          <div className="drawer-header-content">
            <h3>{title}</h3>
            {subtitle && <p className="drawer-subtitle">{subtitle}</p>}
          </div>
          <button className="drawer-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="drawer-content custom-scrollbar">
          <div className="drawer-topics-grid">
            {items.length > 0 ? (
              items.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems[item[itemKey]];
                const isSelected = selectedId && item[itemKey] === selectedId;
                const isChildSelected =
                  hasChildren &&
                  item.children.some((child) => child[itemKey] === selectedId);

                return (
                  <div
                    key={item[itemKey]}
                    className={`drawer-topic-card ${
                      isSelected ? "selected" : ""
                    } ${isChildSelected ? "contains-selected" : ""}`}
                  >
                    <div
                      className="drawer-topic-header"
                      onClick={() => {
                        onItemClick(item);
                        if (!hasChildren) {
                          onClose();
                        }
                      }}
                    >
                      <div className="drawer-topic-info">
                        <h4 className="drawer-topic-title">
                          {item[itemTitle]}
                        </h4>
                        {item[itemSubtitle] && (
                          <p className="drawer-topic-subtitle">
                            {item[itemSubtitle]}
                          </p>
                        )}
                      </div>
                      {hasChildren && (
                        <div
                          className="drawer-topic-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleItem(item[itemKey]);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            className="drawer-topic-icon"
                          />
                        </div>
                      )}
                    </div>

                    {hasChildren && isExpanded && (
                      <div className="drawer-topic-children">
                        {item.children.map((child) => {
                          const isChildSelected =
                            selectedId && child[itemKey] === selectedId;
                          return (
                            <div
                              key={child[itemKey]}
                              className={`drawer-topic-child ${
                                isChildSelected ? "selected" : ""
                              }`}
                              onClick={() => {
                                onItemClick(child);
                                onClose();
                              }}
                            >
                              <span className="drawer-child-title">
                                {child[itemTitle]}
                              </span>
                              <FontAwesomeIcon
                                icon={faChevronRight}
                                className="drawer-child-icon"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="drawer-empty">No items found</div>
            )}
          </div>
        </div>

        <div className="drawer-footer">
          <div className="drawer-footer-left">
            <FontAwesomeIcon icon={faBullseye} className="drawer-footer-icon" />
            <span className="drawer-footer-course">
              {courseName || "Question-Bank"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
